/**
 * Date primitives. The engine speaks in plain `{ year, month, day }` records
 * (1-indexed month 1..12, day 1..31) so every function is deterministic and
 * timezone-agnostic. Conversion to/from ISO strings happens at the edges.
 */
export function fromIso(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m)
        throw new Error(`Invalid ISO date: ${iso}`);
    return {
        year: Number(m[1]),
        month: Number(m[2]),
        day: Number(m[3]),
    };
}
export function toIso(d) {
    return `${String(d.year).padStart(4, '0')}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;
}
/**
 * Days between two dates as integer (exclusive of start, inclusive of end).
 * Both dates must be Gregorian.
 */
export function diffDays(from, to) {
    return Math.round((toUTC(to) - toUTC(from)) / 86_400_000);
}
/** Add `n` days to a Gregorian date and return a new record. */
export function addDays(d, n) {
    const ms = toUTC(d) + n * 86_400_000;
    const dd = new Date(ms);
    return { year: dd.getUTCFullYear(), month: dd.getUTCMonth() + 1, day: dd.getUTCDate() };
}
/** 0 = Sunday, 1 = Monday, …, 6 = Saturday. */
export function dayOfWeek(d) {
    return new Date(Date.UTC(d.year, d.month - 1, d.day)).getUTCDay();
}
/** Whether two dates are the same calendar day. */
export function sameDay(a, b) {
    return a.year === b.year && a.month === b.month && a.day === b.day;
}
/** Whether `a` is strictly before `b`. */
export function isBefore(a, b) {
    return toUTC(a) < toUTC(b);
}
/** Whether `a` is on or after `b`. */
export function isOnOrAfter(a, b) {
    return toUTC(a) >= toUTC(b);
}
function toUTC(d) {
    return Date.UTC(d.year, d.month - 1, d.day);
}
/**
 * Difference in days between the Julian and Gregorian calendars for a given
 * Gregorian year. The Gregorian reform skipped 10 days at its introduction
 * (1582) and adds another day every 400-divisible-not-100 century.
 *
 *  1900..2099 → 13
 *  2100..2199 → 14
 *  2200..2299 → 15
 *  2300..2399 → 16
 *  2400..2499 → 16  (2400 is leap in both)
 *  2500..2599 → 17
 */
export function julianGregorianOffset(year) {
    return Math.floor(year / 100) - Math.floor(year / 400) - 2;
}
/**
 * The *fixed-menaion* lookup date for a calendar style on a civil Gregorian
 * date. Old and New calendars share one menaion and one Paschalion; they
 * differ only in how the fixed cycle maps onto the civil date. New/Revised-
 * Julian keep the civil date; Old (Julian) reckons the fixed cycle behind by
 * the Julian–Gregorian offset, so we shift the lookup back, e.g. civil Jan 7
 * → menaion Dec 25 (Nativity).
 *
 * Movable (Pascha-relative) content must NOT use this, it is identical in
 * both calendars and stays on the civil date.
 */
export function fixedDateFor(date, calendarStyle = 'new') {
    if (calendarStyle === 'old')
        return addDays(date, -julianGregorianOffset(date.year));
    return date;
}
//# sourceMappingURL=dates.js.map